import { HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdPlanetKey } from '../hd-planet-display-config';

/**
 * Structurele configuratie voor de "Resonance"-variant van de Lunar Planetary
 * Square (advanced LPS). Bevat enkel de structuur (welke planeten, welke groep,
 * welk base-nummer) + de i18n-sleutels. Alle zichtbare tekst leeft in de
 * Transloco-bestanden onder de scope `humanDesign.resonanceLps.*` (nl + en) en
 * wordt via de HD content-language-override vertaald (zie
 * `.\docs\human-design\hd-advanced-lunar-planetary-square.md`).
 */

const I18N_ROOT = 'humanDesign.resonanceLps';

/** i18n-sleutel voor de zichtbare (bold) keynote van een LPS-blok. */
export function resonanceKeynoteKey(planet: HdPlanetKey, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.blocks.${planet}.${side}.keynote`;
}

/** i18n-sleutel voor de tooltip (klein lettertype) bij de keynote van een LPS-blok. */
export function resonanceTooltipKey(planet: HdPlanetKey, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.blocks.${planet}.${side}.tooltip`;
}

/** De drie verticale groeperingen (kolom-triplets) van de LPS. */
export type HdResonanceVerticalKey = 'internalBalance' | 'externalized' | 'oxygen';

export interface HdResonanceVerticalConfig {
  readonly key: HdResonanceVerticalKey;
  /** De planeten in deze kolom, geordend rij 1 → rij 3. */
  readonly planetKeys: readonly [HdPlanetKey, HdPlanetKey, HdPlanetKey];
}

export const HD_RESONANCE_VERTICALS: readonly HdResonanceVerticalConfig[] = [
  { key: 'internalBalance', planetKeys: ['moon', 'venus', 'saturn'] },
  { key: 'externalized', planetKeys: ['mercury', 'mars', 'jupiter'] },
  { key: 'oxygen', planetKeys: ['uranus', 'neptune', 'pluto'] },
];

export function resonanceVerticalLabelKey(key: HdResonanceVerticalKey, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.verticals.${key}.${side}.label`;
}

export function resonanceVerticalKeynoteKey(key: HdResonanceVerticalKey, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.verticals.${key}.${side}.keynote`;
}

export function resonanceVerticalDescriptionKey(key: HdResonanceVerticalKey, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.verticals.${key}.${side}.description`;
}

/** De drie laterale groeperingen (rijen) van de LPS. */
export const HD_RESONANCE_LATERAL_ROWS = [1, 2, 3] as const;
export type HdResonanceLateralRow = (typeof HD_RESONANCE_LATERAL_ROWS)[number];

export function resonanceLateralLabelKey(row: HdResonanceLateralRow, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.laterals.row${row}.${side}.label`;
}

export function resonanceLateralTooltipKey(row: HdResonanceLateralRow, side: HdBodygraphSide): string {
  return `${I18N_ROOT}.laterals.row${row}.${side}.tooltip`;
}

/**
 * Base-nummer (1–5, "5 Dimensional Fields") dat op elk base-dragend LPS-blok
 * getoond wordt, per zijde. Afgeleid uit de mat-mapping in
 * `.\docs\human-design\base-logic.md`:
 * - Design Mat (Prana-diagonaal): Pluto=1, Uranus=2, Mars=3, Moon=4, Saturn=5.
 * - Personality Mat (Marsiaanse diagonaal): Pluto=1, Moon=2, Saturn=3, Uranus=4, Mars=5.
 */
export const HD_RESONANCE_BASE_BY_PLANET: Readonly<
  Record<'design' | 'personality', Readonly<Partial<Record<HdPlanetKey, number>>>>
> = {
  design: { pluto: 1, uranus: 2, mars: 3, moon: 4, saturn: 5 },
  personality: { pluto: 1, moon: 2, saturn: 3, uranus: 4, mars: 5 },
};

export function resonanceBaseNameKey(base: number): string {
  return `${I18N_ROOT}.bases.${base}.name`;
}

/**
 * Asset-pad naar het HD-base-icoon (1–5, "5 Dimensional Fields") dat als
 * symbool in de hoek van een LPS-blok toont. SVG's komen uit Figma
 * (`1821-793`) en staan in `src/assets/img/human-design/base-icons/`.
 */
const HD_RESONANCE_BASE_ICON_BY_NUMBER: Readonly<Record<number, string>> = {
  1: 'base-1-movement.svg',
  2: 'base-2-evolution.svg',
  3: 'base-3-being.svg',
  4: 'base-4-design.svg',
  5: 'base-5-space.svg',
};

export function resonanceBaseIconSrc(base: number): string | null {
  const fileName = HD_RESONANCE_BASE_ICON_BY_NUMBER[base];
  return fileName ? `assets/img/human-design/base-icons/${fileName}` : null;
}

export function resonanceBaseKeynoteKey(base: number): string {
  return `${I18N_ROOT}.bases.${base}.keynote`;
}

/** Legende-sleutels. */
export function resonanceLegendTitleKey(side: HdBodygraphSide): string {
  return `${I18N_ROOT}.legend.title.${side}`;
}

/**
 * Onderdelen van de prana-indicator (rechtsboven): het woord "Prana", de
 * OUT/IN-richtingslabels en de bijhorende as-omschrijvingen (laterals =
 * prana uit / expressie naar buiten, verticals = prana in / basis van de
 * levensenergie). Uit Figma-frame `2171-9393`.
 */
export type HdResonancePranaPart =
  | 'word'
  | 'out'
  | 'in'
  | 'lateralReactivity'
  | 'verticalShaping';

export function resonancePranaKey(part: HdResonancePranaPart): string {
  return `${I18N_ROOT}.prana.${part}`;
}

export const HD_RESONANCE_LEGEND = {
  presence: `${I18N_ROOT}.legend.presence`,
  prana: `${I18N_ROOT}.legend.prana`,
  pranaIn: `${I18N_ROOT}.legend.pranaIn`,
  pranaOut: `${I18N_ROOT}.legend.pranaOut`,
  vertical: `${I18N_ROOT}.legend.vertical`,
  lateral: `${I18N_ROOT}.legend.lateral`,
  verticals: `${I18N_ROOT}.legend.verticals`,
  laterals: `${I18N_ROOT}.legend.laterals`,
  mundaneConsciousness: `${I18N_ROOT}.legend.mundaneConsciousness`,
  martianDiagonals: `${I18N_ROOT}.legend.martianDiagonals`,
} as const;

/**
 * Asset-paden naar de legende-mini-grid-iconen uit Figma (`2171-9407/9412/9193`),
 * gedownload naar `src/assets/img/human-design/legend-icons/`. Mundane
 * consciousness gebruikt hetzelfde icoon als presence (Figma-conform).
 */
const LEGEND_ICON_DIR = 'assets/img/human-design/legend-icons';

export const HD_RESONANCE_LEGEND_ICON_SRC = {
  presence: `${LEGEND_ICON_DIR}/presence.svg`,
  prana: `${LEGEND_ICON_DIR}/prana.svg`,
  mundaneConsciousness: `${LEGEND_ICON_DIR}/presence.svg`,
  martianDiagonals: `${LEGEND_ICON_DIR}/martian-diagonals.svg`,
} as const;

/** De legende-item-soort bepaalt icoon (SVG-img of kleur-swatch) + tekstkleur. */
export type HdResonanceLegendKind =
  | 'presence'
  | 'prana'
  | 'mundaneConsciousness'
  | 'martianDiagonals'
  | 'vertical'
  | 'lateral';
